import { useState } from "react";
import { Link } from "react-router-dom";
import { useTodayRecovery } from "@/hooks/use-today-recovery";
import type { ExertionZone } from "@/lib/recovery/formula";

/**
 * Recovery hero card on /today (BW18 PR-E).
 *
 * Big number with color band + 1-line training recommendation. Tap to
 * expand the component breakdown (HRV / Sleep / RHR / Exertion / EA) or to
 * navigate to /health/recovery for the full trend.
 */
export function RecoveryHeroCard() {
  const q = useTodayRecovery();
  const [expanded, setExpanded] = useState(false);

  if (q.isLoading) {
    return <SkeletonCard />;
  }

  if (!q.data) {
    return (
      <div style={cardStyle()}>
        <p style={{ margin: 0, color: "var(--fg-2)", fontSize: "var(--type-body-s)" }}>
          Recovery score will compute once vitals + sleep have synced for today.
        </p>
      </div>
    );
  }

  if (q.data.data_quality === "insufficient" || q.data.score == null) {
    return <InsufficientCard available={q.data.available_components} />;
  }

  const { score, zone, components, source } = q.data;
  const color = zone ? bandColor(zone) : "var(--fg-3)";

  return (
    <section
      aria-labelledby="recovery-hero-heading"
      style={{
        ...cardStyle(),
        borderLeft: `4px solid ${color}`,
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: "var(--space-3)",
        }}
      >
        <div>
          <p
            id="recovery-hero-heading"
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "var(--type-micro)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--fg-3)",
              margin: 0,
            }}
          >
            Recovery
          </p>
          <p
            style={{
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              fontSize: 56,
              fontWeight: 500,
              lineHeight: 1,
              margin: "var(--space-1) 0 0",
              color: "var(--fg)",
            }}
          >
            {score}
            <span style={{ fontSize: 24, color: "var(--fg-3)" }}> / 100</span>
          </p>
        </div>
        <Link
          to="/health/recovery"
          style={{
            color: "var(--fg-3)",
            fontSize: "var(--type-meta)",
            textDecoration: "underline",
            textDecorationColor: "var(--hair-2)",
          }}
        >
          Trend
        </Link>
      </header>

      {zone && (
        <p
          style={{
            margin: "var(--space-3) 0 0",
            fontSize: "var(--type-body)",
            color: "var(--fg)",
          }}
        >
          {zone.recommendation}
        </p>
      )}

      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        style={expandButtonStyle()}
        aria-expanded={expanded}
      >
        {expanded ? "Hide breakdown" : "Show breakdown"}
      </button>

      {expanded && components && (
        <dl style={breakdownStyle()}>
          <ComponentRow label="HRV" value={components.hrv_z_component} suffix="/100" />
          <ComponentRow label="Sleep" value={components.sleep_component} suffix="/100" />
          <ComponentRow label="RHR" value={components.rhr_z_component} suffix="/100" />
          <ComponentRow
            label="Training load"
            value={components.load_component}
            suffix="/100"
          />
          <ComponentRow label="EA component" value={components.ea_component} suffix="/100" />
          <ComponentRow
            label="Subjective"
            value={components.subjective_component}
            suffix="/100"
          />
          {components.ea_kcal_per_kg_ffm != null && (
            <ComponentRow
              label="EA today"
              value={components.ea_kcal_per_kg_ffm}
              suffix=" kcal/kg FFM"
              status={components.ea_status}
            />
          )}
          {components.override_reason && (
            <OverrideBadge reason={components.override_reason} />
          )}
        </dl>
      )}

      {source === "client" && (
        <p
          style={{
            marginTop: "var(--space-3)",
            fontSize: "var(--type-meta)",
            color: "var(--fg-3)",
          }}
        >
          Computed in-browser. The daily Worker compute writes a precomputed
          score after 5am.
        </p>
      )}
    </section>
  );
}

function InsufficientCard({ available }: { available: number }) {
  return (
    <section style={cardStyle()} aria-label="Recovery">
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: 0,
        }}
      >
        Recovery
      </p>
      <p
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontSize: 32,
          fontWeight: 500,
          lineHeight: 1.1,
          margin: "var(--space-2) 0 0",
          color: "var(--fg)",
        }}
      >
        Need more data
      </p>
      <p
        style={{
          margin: "var(--space-3) 0 var(--space-4)",
          color: "var(--fg-2)",
          fontSize: "var(--type-body-s)",
          lineHeight: 1.5,
        }}
      >
        Recovery Score needs at least 3 of 5 inputs (HRV, sleep, RHR, exertion,
        energy availability). Currently {available} available. Connect a sensor
        or log a meal / workout and the score appears once HAE syncs.
      </p>
      <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
        <Link
          to="/integrations"
          style={ctaStyle()}
        >
          Connect HAE →
        </Link>
        <Link
          to="/health/nutrition"
          style={{
            ...ctaStyle(),
            background: "transparent",
            color: "var(--fg)",
            border: "1px solid var(--hair-2)",
          }}
        >
          Log a meal →
        </Link>
        <Link
          to="/health/training"
          style={{
            ...ctaStyle(),
            background: "transparent",
            color: "var(--fg)",
            border: "1px solid var(--hair-2)",
          }}
        >
          Log a workout →
        </Link>
      </div>
    </section>
  );
}

function ctaStyle(): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    height: 32,
    padding: "0 12px",
    background: "var(--accent)",
    color: "#0a0b0d",
    border: "none",
    borderRadius: "var(--radius-card)",
    fontSize: "var(--type-meta)",
    fontWeight: 500,
    textDecoration: "none",
  };
}

function SkeletonCard() {
  return (
    <div style={cardStyle()}>
      <p style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
        Computing recovery…
      </p>
    </div>
  );
}

function OverrideBadge({ reason }: { reason: "ea_problematic" | "ea_risk" | "illness" }) {
  const label =
    reason === "ea_problematic"
      ? "RED-S risk flag — EA <25"
      : reason === "ea_risk"
        ? "RED-S risk flag — EA <30"
        : "Illness signal active";
  const meaning =
    reason === "illness"
      ? "Recent vitals flag possible illness. Score capped at 50 until signals normalize."
      : reason === "ea_problematic"
        ? "Energy availability is in the problematic range. Score capped at 40 until 3-day average recovers."
        : "Energy availability is below the risk threshold. Score capped at 55 until 3-day average recovers.";
  return (
    <div
      style={{
        marginTop: "var(--space-2)",
        padding: "var(--space-3)",
        background: "var(--accent-wash)",
        border: "1px solid var(--accent-dim)",
        borderRadius: "var(--radius-card)",
        fontSize: "var(--type-body-s)",
        color: "var(--fg)",
        lineHeight: 1.5,
      }}
    >
      <p style={{ margin: 0, fontWeight: 500 }}>{label}</p>
      <p style={{ margin: "4px 0 0", color: "var(--fg-2)" }}>{meaning}</p>
    </div>
  );
}

function ComponentRow({
  label,
  value,
  suffix,
  status,
}: {
  label: string;
  value: number | null;
  suffix: string;
  status?: string | null;
}) {
  if (value == null) {
    return (
      <div style={rowStyle()}>
        <dt style={dtStyle()}>{label}</dt>
        <dd style={ddStyle()}>—</dd>
      </div>
    );
  }
  const statusBadge =
    status === "problematic"
      ? { label: "Problematic LEA", color: "var(--brick)" }
      : status === "risk"
        ? { label: "Risk zone", color: "var(--accent)" }
        : status === "adequate"
          ? { label: "Adequate", color: "var(--fg-2)" }
          : status === "optimal"
            ? { label: "Optimal", color: "var(--accent)" }
            : null;

  return (
    <div style={rowStyle()}>
      <dt style={dtStyle()}>{label}</dt>
      <dd style={ddStyle()}>
        {value}
        {suffix}
        {statusBadge && (
          <span
            style={{
              marginLeft: "var(--space-2)",
              fontSize: "var(--type-meta)",
              color: statusBadge.color,
            }}
          >
            {statusBadge.label}
          </span>
        )}
      </dd>
    </div>
  );
}

function bandColor(zone: ExertionZone): string {
  switch (zone.color) {
    case "red":
      return "var(--brick)";
    case "orange":
      return "var(--accent)";
    case "yellow":
      return "var(--accent)";
    case "light-green":
    case "green":
      return "var(--accent)";
  }
}

function cardStyle(): React.CSSProperties {
  return {
    background: "var(--bg-2)",
    border: "1px solid var(--hair)",
    borderRadius: "var(--radius-surface)",
    padding: "var(--space-5) var(--space-6)",
  };
}

function expandButtonStyle(): React.CSSProperties {
  return {
    marginTop: "var(--space-4)",
    background: "transparent",
    border: "none",
    color: "var(--fg-3)",
    fontSize: "var(--type-meta)",
    textDecoration: "underline",
    textDecorationColor: "var(--hair-2)",
    cursor: "pointer",
    padding: 0,
  };
}

function breakdownStyle(): React.CSSProperties {
  return {
    marginTop: "var(--space-4)",
    paddingTop: "var(--space-4)",
    borderTop: "1px solid var(--hair-2)",
    display: "grid",
    gap: "var(--space-2)",
  };
}

function rowStyle(): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "var(--space-3)",
  };
}

function dtStyle(): React.CSSProperties {
  return { color: "var(--fg-2)", fontSize: "var(--type-body-s)" };
}

function ddStyle(): React.CSSProperties {
  return {
    margin: 0,
    color: "var(--fg)",
    fontSize: "var(--type-body-s)",
    fontVariantNumeric: "tabular-nums",
  };
}
